import React, { useState, useEffect } from 'react';
import { UserRole } from '../types/property.types';
import { useAuth } from '../contexts/AuthContext';
import UserForm from './UserForm';

interface User {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  avatar?: string;
  active: boolean;
  createdAt: Date;
  lastLogin?: Date;
}

const UserManagement: React.FC = () => {
  const { user: currentUser, canEdit } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [isFormLoading, setIsFormLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState<UserRole | ''>('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');

  // Simular carga inicial de usuarios
  useEffect(() => {
    const mockUsers: User[] = [
      {
        id: '1',
        name: 'Oscar Cuellar',
        email: 'oscar.cuellar@crearia.co',
        role: UserRole.ADMIN,
        avatar: 'https://ui-avatars.com/api/?name=Oscar+Cuellar&background=3b82f6&color=fff',
        active: true,
        createdAt: new Date('2024-01-15'),
        lastLogin: new Date('2025-07-08')
      },
      {
        id: '4',
        name: 'Héctor Villada',
        email: 'hectorvillada@prointegral.com',
        role: UserRole.ADMIN,
        avatar: 'https://ui-avatars.com/api/?name=Hector+Villada&background=8b5cf6&color=fff',
        active: true,
        createdAt: new Date('2024-02-01'),
        lastLogin: new Date('2025-07-07')
      },
      {
        id: '2',
        name: 'Ana García',
        email: 'ana.garcia@inmobiliaria.com',
        role: UserRole.COMERCIAL,
        avatar: 'https://ui-avatars.com/api/?name=Ana+Garcia&background=10b981&color=fff',
        active: true,
        createdAt: new Date('2024-03-10'),
        lastLogin: new Date('2025-07-06')
      },
      {
        id: '5',
        name: 'María Residencial',
        email: 'maria.residencial@inmobiliaria.com',
        role: UserRole.RESIDENCIAL,
        avatar: 'https://ui-avatars.com/api/?name=Maria+Residencial&background=ec4899&color=fff',
        active: true,
        createdAt: new Date('2024-04-05'),
        lastLogin: new Date('2025-07-05')
      },
      {
        id: '6',
        name: 'Juan Ventas',
        email: 'juan.ventas@inmobiliaria.com',
        role: UserRole.VENTAS,
        avatar: 'https://ui-avatars.com/api/?name=Juan+Ventas&background=14b8a6&color=fff',
        active: true,
        createdAt: new Date('2024-05-12'),
        lastLogin: new Date('2025-07-04')
      },
      {
        id: '3',
        name: 'Carlos Ruiz',
        email: 'carlos.ruiz@callcenter.com',
        role: UserRole.CALL_CENTER,
        avatar: 'https://ui-avatars.com/api/?name=Carlos+Ruiz&background=f59e0b&color=fff',
        active: true,
        createdAt: new Date('2024-06-20'),
        lastLogin: new Date('2025-07-03')
      },
      {
        id: '7',
        name: 'Sofia Asesora',
        email: 'sofia.asesora@inmobiliaria.com',
        role: UserRole.ASESOR,
        avatar: 'https://ui-avatars.com/api/?name=Sofia+Asesora&background=84cc16&color=fff',
        active: false,
        createdAt: new Date('2024-07-01'),
        lastLogin: new Date('2025-06-15')
      }
    ];

    setTimeout(() => {
      setUsers(mockUsers);
      setLoading(false);
    }, 1000);
  }, []);

  // Solo los administradores pueden acceder
  if (!canEdit) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <div className="text-4xl mb-4">🚫</div>
        <h2 className="text-xl font-semibold text-red-800 mb-2">Acceso Denegado</h2>
        <p className="text-red-600">Solo los administradores pueden gestionar usuarios.</p>
      </div>
    );
  }

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = !filterRole || user.role === filterRole;
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'active' && user.active) ||
                         (filterStatus === 'inactive' && !user.active);
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  const handleCreateUser = () => {
    setEditingUser(null);
    setShowForm(true);
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setShowForm(true);
  };

  const handleDeleteUser = async (user: User) => {
    if (user.id === currentUser?.id) {
      alert('No puedes eliminar tu propia cuenta');
      return;
    }

    const confirmed = window.confirm(
      `¿Estás seguro de que quieres eliminar al usuario "${user.name}"?`
    );
    
    if (confirmed) {
      setUsers(prev => prev.filter(u => u.id !== user.id));
    }
  };

  const handleToggleUserStatus = async (user: User) => {
    if (user.id === currentUser?.id) {
      alert('No puedes desactivar tu propia cuenta');
      return;
    }

    setUsers(prev => prev.map(u => 
      u.id === user.id ? { ...u, active: !u.active } : u
    ));
  };

  const handleSaveUser = async (userData: Partial<User>): Promise<boolean> => {
    setIsFormLoading(true);
    
    try {
      // Simular delay de guardado
      await new Promise(resolve => setTimeout(resolve, 1000));

      if (editingUser) {
        // Actualizar usuario existente
        setUsers(prev => prev.map(u => 
          u.id === editingUser.id ? { ...u, ...userData } : u
        ));
      } else {
        // Crear nuevo usuario
        const newUser: User = {
          id: Date.now().toString(),
          name: userData.name!,
          email: userData.email!,
          role: userData.role!,
          avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(userData.name!)}&background=random&color=fff`,
          active: userData.active ?? true,
          createdAt: new Date(),
          lastLogin: undefined
        };
        setUsers(prev => [...prev, newUser]);
      }
      
      return true;
    } catch (error) {
      console.error('Error al guardar usuario:', error);
      return false;
    } finally {
      setIsFormLoading(false);
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingUser(null);
  };

  const getRoleDisplay = (role: UserRole) => {
    const roleConfig = {
      [UserRole.ADMIN]: { label: 'Administrador', color: 'bg-blue-100 text-blue-800', icon: '👑' },
      [UserRole.COMERCIAL]: { label: 'Comercial', color: 'bg-orange-100 text-orange-800', icon: '🏢' },
      [UserRole.RESIDENCIAL]: { label: 'Residencial', color: 'bg-green-100 text-green-800', icon: '🏠' },
      [UserRole.VENTAS]: { label: 'Ventas', color: 'bg-purple-100 text-purple-800', icon: '💰' },
      [UserRole.CALL_CENTER]: { label: 'Call Center', color: 'bg-yellow-100 text-yellow-800', icon: '📞' },
      [UserRole.ASESOR]: { label: 'Asesor', color: 'bg-gray-100 text-gray-800', icon: '👔' }
    };
    return roleConfig[role] || { label: role, color: 'bg-gray-100 text-gray-800', icon: '👤' };
  };

  const getUserStats = () => {
    const total = users.length;
    const active = users.filter(u => u.active).length;
    const admins = users.filter(u => u.role === UserRole.ADMIN).length;
    const comercial = users.filter(u => u.role === UserRole.COMERCIAL).length;
    const residencial = users.filter(u => u.role === UserRole.RESIDENCIAL).length;
    const ventas = users.filter(u => u.role === UserRole.VENTAS).length;
    const callCenter = users.filter(u => u.role === UserRole.CALL_CENTER).length;
    const asesor = users.filter(u => u.role === UserRole.ASESOR).length;

    return { total, active, admins, comercial, residencial, ventas, callCenter, asesor };
  };

  const stats = getUserStats();

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Gestión de Usuarios</h2>
          <p className="text-gray-600">Administra los usuarios y sus permisos en el sistema</p>
        </div>
        <button
          onClick={handleCreateUser}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          <span>Nuevo Usuario</span>
        </button>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-blue-600">{stats.total}</div>
          <div className="text-sm text-gray-600">Total</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-green-600">{stats.active}</div>
          <div className="text-sm text-gray-600">Activos</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-blue-600">{stats.admins}</div>
          <div className="text-sm text-gray-600">👑 Admin</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-orange-600">{stats.comercial}</div>
          <div className="text-sm text-gray-600">🏢 Comercial</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-green-600">{stats.residencial}</div>
          <div className="text-sm text-gray-600">🏠 Residencial</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-purple-600">{stats.ventas}</div>
          <div className="text-sm text-gray-600">💰 Ventas</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-yellow-600">{stats.callCenter}</div>
          <div className="text-sm text-gray-600">📞 Call Center</div>
        </div>
        <div className="bg-white rounded-lg shadow p-4 text-center">
          <div className="text-2xl font-bold text-gray-600">{stats.asesor}</div>
          <div className="text-sm text-gray-600">👔 Asesor</div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Buscar usuarios</label>
            <div className="relative">
              <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por nombre o email..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Filtrar por rol</label>
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value as UserRole | '')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Todos los roles</option>
              <option value={UserRole.ADMIN}>👑 Administrador</option>
              <option value={UserRole.COMERCIAL}>🏢 Comercial</option>
              <option value={UserRole.RESIDENCIAL}>🏠 Residencial</option>
              <option value={UserRole.VENTAS}>💰 Ventas</option>
              <option value={UserRole.CALL_CENTER}>📞 Call Center</option>
              <option value={UserRole.ASESOR}>👔 Asesor</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Filtrar por estado</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as 'all' | 'active' | 'inactive')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Todos</option>
              <option value="active">Activos</option>
              <option value="inactive">Inactivos</option>
            </select>
          </div>
        </div>
      </div>

      {/* Lista de usuarios */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Usuario
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rol
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Último acceso
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map((user) => {
                const roleConfig = getRoleDisplay(user.role);
                return (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <img
                          className="h-10 w-10 rounded-full"
                          src={user.avatar}
                          alt={user.name}
                        />
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{user.name}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${roleConfig.color}`}>
                        {roleConfig.icon} {roleConfig.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {user.active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.lastLogin ? user.lastLogin.toLocaleDateString() : 'Nunca'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditUser(user)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => handleToggleUserStatus(user)}
                          className={`${user.active ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'}`}
                          disabled={user.id === currentUser?.id}
                        >
                          {user.active ? 'Desactivar' : 'Activar'}
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user)}
                          className="text-red-600 hover:text-red-900"
                          disabled={user.id === currentUser?.id}
                        >
                          Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">👥</div>
            <p className="text-gray-500">No se encontraron usuarios</p>
          </div>
        )}
      </div>

      {/* Formulario de usuario */}
      {showForm && (
        <UserForm
          user={editingUser}
          onSave={handleSaveUser}
          onCancel={handleCloseForm}
          isLoading={isFormLoading}
        />
      )}
    </div>
  );
};

export default UserManagement;